import * as React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body text / description of what's about to happen. */
  body?: React.ReactNode;
  /** Confirm button label (default "Confirm"). */
  confirmLabel?: string;
  /** Cancel button label (default "Cancel"). */
  cancelLabel?: string;
  /** When true, the confirm button is styled as a destructive (error) action. */
  destructive?: boolean;
  /** The action to run on confirm. May be async. If it throws, the error is
   *  shown inline in the dialog and the dialog stays open. On success the
   *  caller is responsible for closing (usually via onClose in onConfirm). */
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

/**
 * A small in-page confirmation dialog — the replacement for `window.confirm`
 * / `window.alert`. Handles the async confirm flow: shows a spinner while the
 * action runs, surfaces any thrown error inline (instead of a native alert),
 * and keeps the dialog open on failure so the user can retry or cancel.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Clear transient state whenever the dialog (re)opens.
  React.useEffect(() => {
    if (open) {
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      // Success: the caller closes the dialog (it owns `open`).
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {body && <DialogContentText component="div">{body}</DialogContentText>}
        {error && (
          <Alert severity="error" sx={{ mt: body ? 2 : 0 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={busy}
          variant="contained"
          color={destructive ? "error" : "primary"}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
